import { useEffect, useState } from "react";
import api from "@/lib/api";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";
import { UserPlus, Trash2 } from "lucide-react";

export default function AdminUsers() {
  const { t } = useLang();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "staff" });
  const { user: me } = useAuth();

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("Staff added");
      setForm({ email: "", name: "", password: "", role: "staff" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this user?")) return;
    await api.delete(`/users/${id}`);
    load();
  };

  return (
    <StaffLayout>
      <div className="overline text-zinc-500">{t("admin")}</div>
      <h1 className="font-condensed text-5xl font-bold tracking-tight mt-1">{t("staff_title")}</h1>

      <form onSubmit={create} className="mt-8 grid md:grid-cols-5 gap-3 items-end" data-testid="new-user-form">
        <div className="md:col-span-2">
          <label className="overline text-zinc-500">{t("email")}</label>
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 px-3 py-2 w-full font-plex text-sm" data-testid="new-user-email" />
        </div>
        <div>
          <label className="overline text-zinc-500">{t("name_lbl")}</label>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 px-3 py-2 w-full font-plex text-sm" data-testid="new-user-name" />
        </div>
        <div>
          <label className="overline text-zinc-500">{t("password")}</label>
          <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2 px-3 py-2 w-full font-plex text-sm" data-testid="new-user-password" />
        </div>
        <div>
          <label className="overline text-zinc-500">{t("role_lbl")}</label>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-2 px-3 py-2 w-full font-plex text-sm" data-testid="new-user-role">
            <option value="staff">{t("role_staff")}</option>
            {me?.role === "owner" && <option value="admin">{t("role_admin")}</option>}
          </select>
        </div>
        <button type="submit" data-testid="add-user-btn" className="md:col-span-5 bg-white text-black px-4 py-2 font-plex text-sm hover:bg-[#007aff] hover:text-white transition-colors flex items-center justify-center gap-2">
          <UserPlus size={14} /> {t("add_user")}
        </button>
      </form>

      <div className="mt-10 border border-[#262626]">
        <div className="grid grid-cols-12 px-4 py-3 border-b border-[#262626] overline text-zinc-500">
          <div className="col-span-4">{t("email")}</div>
          <div className="col-span-3">{t("name_lbl") || "Name"}</div>
          <div className="col-span-2">{t("role_lbl") || "Role"}</div>
          <div className="col-span-3 text-right">{t("actions")}</div>
        </div>
        {users.map((u) => (
          <div key={u.id} className="grid grid-cols-12 px-4 py-3 border-b border-[#262626] last:border-0 items-center font-plex text-sm" data-testid={`user-row-${u.id}`}>
            <div className="col-span-4 truncate">{u.email}</div>
            <div className="col-span-3 text-zinc-400">{u.name}</div>
            <div className="col-span-2"><span className="overline" style={{ color: u.role === "owner" ? "#ffd60a" : u.role === "admin" ? "#c85a40" : "#a1a1aa" }}>{t(u.role === "owner" ? "role_owner" : u.role === "admin" ? "role_admin" : "role_staff")}</span></div>
            <div className="col-span-3 flex justify-end">
              {u.id !== me?.id && (
                <button onClick={() => remove(u.id)} data-testid={`delete-user-${u.id}`} className="border border-[#262626] px-3 py-1.5 text-xs hover:bg-[#ff3b30] hover:text-white hover:border-[#ff3b30] transition-colors flex items-center gap-1"><Trash2 size={12} /> {t("remove")}</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </StaffLayout>
  );
}
